// btw-front/src/lib/customerBusinessNumber.js
// 고객사 사업자등록번호 — 딜 폼 공용 (등록/수정/관리자 폼 3곳).
//
// 파트너는 고객사의 사업자번호를 모른다. 그래서 직접 입력받지 않고 **공공데이터가 준 앞자리를
// 그대로 채워** 두고, 뒷자리만 아는 경우에 한해 선택 입력으로 덧붙이게 한다.
//   앞자리(prefix) — 고객사 검색 결과의 값. 읽기 전용. **항상 6자리**(2026-09-29 실측 확인).
//   뒷자리(suffix) — 사용자가 알면 넣는 선택 입력. 앞자리와 합쳐 최대 10자리.
//
// 이 값은 **등록을 막지 않는다.** 차단은 고객사 이름으로만 하고, 번호는 중복 확인을 거드는
// 보조 신호로만 쓴다(앞자리만으로는 여러 법인이 겹칠 수 있어 확신할 수 없다).
// 그래서 체크섬이 틀려도 저장은 되며, 화면에서 확인만 권한다.

import { isValidBusinessNumber } from "./partnerSignupValidation";

/** 사업자등록번호 전체 자릿수(3-2-5) */
export const BUSINESS_NUMBER_LENGTH = 10;

/**
 * 공공데이터(국민연금 사업장)가 주는 앞자리 길이 — 6자리 고정.
 * 저장값의 앞 6자리는 늘 조회분, 나머지 4자리는 사용자가 덧붙인 몫이다.
 * 그래서 화면 상태로 들고 다니지 않고 상수로 쓴다(수정 화면에서도 같은 기준이 선다).
 */
export const PUBLIC_PREFIX_LENGTH = 6;

/** 숫자만 남기고 10자리로 컷 */
export function normalizeCustomerBusinessNumber(input) {
  return String(input == null ? "" : input)
    .replace(/\D/g, "")
    .slice(0, BUSINESS_NUMBER_LENGTH);
}

/**
 * 저장값에서 앞자리(공공데이터분)와 뒷자리(사용자 입력분)를 나눈다.
 * prefixLength 는 고객사 선택 시점에 받은 앞자리 길이 — 그만큼이 읽기 전용 구간이다.
 */
export function splitBusinessNumber(value, prefixLength) {
  const digits = normalizeCustomerBusinessNumber(value);
  const cut = Math.min(Math.max(Number(prefixLength) || 0, 0), digits.length);
  return { prefix: digits.slice(0, cut), suffix: digits.slice(cut) };
}

/** 앞자리 + 뒷자리 → 저장값. 합쳐서 10자리를 넘지 않는다. */
export function joinBusinessNumber(prefix, suffix) {
  return normalizeCustomerBusinessNumber(
    `${normalizeCustomerBusinessNumber(prefix)}${normalizeCustomerBusinessNumber(suffix)}`,
  );
}

/** 뒷자리로 더 받을 수 있는 자릿수 */
export function remainingDigits(prefix) {
  return Math.max(BUSINESS_NUMBER_LENGTH - normalizeCustomerBusinessNumber(prefix).length, 0);
}

/**
 * 10자리를 채웠는데 국세청 체크섬이 어긋나는가.
 * 등록을 막지는 않고 "확인해보라" 는 안내만 띄우는 용도다.
 */
export function isBusinessNumberSuspicious(value) {
  const digits = normalizeCustomerBusinessNumber(value);
  if (digits.length !== BUSINESS_NUMBER_LENGTH) return false; // 미완성은 판단 보류
  return !isValidBusinessNumber(digits);
}

/** 10자리를 다 채웠는가 */
export function isBusinessNumberComplete(value) {
  return normalizeCustomerBusinessNumber(value).length === BUSINESS_NUMBER_LENGTH;
}

/**
 * 표시용 포맷 — **10자리를 다 채웠을 때만** 3-2-5 로 끊는다.
 * 앞 6자리만 있을 때 끊으면 '120-81-4' 가 되어 마지막 묶음이 시작된 것처럼 보인다.
 * 미완성 값은 숫자 그대로 두는 편이 오해가 없다.
 */
export function formatCustomerBusinessNumber(input) {
  const d = normalizeCustomerBusinessNumber(input);
  if (d.length !== BUSINESS_NUMBER_LENGTH) return d;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

// 공공데이터 레코드(meta)에서 가져올 앞자리 — 받은 그대로 채운다(실측상 6자리).
//   자릿수·체크섬을 가리지 않는다. 이 값은 차단에 쓰이지 않으므로 거짓 차단이 생기지 않는다.
export function businessNumberFromCompanyMeta(meta) {
  return normalizeCustomerBusinessNumber(meta && meta.businessNumberPrefix);
}
